"use client";

import { useState } from "react";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import {
  ChevronDown,
  ChevronUp,
  Check,
  Shield,
  Sparkles,
  Coins,
  Flame,
  Crown,
  Zap,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useMetadata } from "@/hooks/useMetadata";
import PublicLayout from "@/components/PublicLayout";
import { usePageContent } from "@/hooks/usePageContent";
import PublicHero from "@/components/PublicHero";

export const PRICING_COPY = {
  hero: {
    eyebrow: "Plans & Credits",
    title: "Pay For Questions,\nNot Promises",
    subtitle:
      "Every answer from the Uni AI pipeline costs exactly one question credit. No subscriptions you forget to cancel, no fine print. Here is precisely what each tier does — and why the 2-week Exam Pass is the one smart students buy.",
  },
  plans: [
    {
      id: "free",
      name: "Free Tier",
      tagline: "For the daily skim",
      price: "₦0",
      pricePeriod: "",
      priceAlt: "5 questions / day · 3 / hour",
      icon: "spark",
      what: "Five questions a day, three per hour. Resets to exactly five every midnight, no rollover. All study tools — notes (view-only), glossary, formula sheets, 10 flashcards/day — stay open, plus 20 PDF downloads a month.",
      why: "You are testing the waters or studying casually. When five questions covers your day, paying is wasting money.",
      features: [
        "5 questions per day (max 3 per hour)",
        "Notes, glossary, formula sheets — all free to view",
        "10 flashcards / day · 2 audio summaries / month",
        "20 PDF downloads / month · 20 image exports",
      ],
      cta: "Get Started Free",
      ctaLink: "/auth/signup",
      recommended: false,
    },
    {
      id: "topup",
      name: "Top-Up",
      tagline: "For the steady grinder",
      price: "₦500",
      pricePeriod: "for 10 questions",
      priceAlt: "Credits expire in 24 hours",
      icon: "coins",
      what: "Buy 10 question credits outright. They stack on any plan and do not change your hourly cap. They expire 24 hours after purchase — a failed generation is still free to retry, so you only spend on answers that land.",
      why: "You need more than your daily allowance most days, but not a firehose. Top-Up is the quiet, pay-as-you-go option. Use them within the day or they vanish.",
      features: [
        "Adds to Free, Exam Pass, or Premium",
        "10 question credits (expire in 24h)",
        "Priority generation in peak hours",
        "No rollover — urgency by design",
      ],
      cta: "Top Up Now",
      ctaLink: "/pay",
      recommended: false,
    },
    {
      id: "exampass",
      name: "Exam Pass",
      tagline: "For exam season — the best value",
      price: "₦2,000",
      pricePeriod: "2 weeks access",
      priceAlt: "15 questions / day · 5 / hour",
      icon: "flame",
      what: "Two weeks of exam firepower: 15 questions a day, 5 per hour, full notes AI (summarise, explain, generate practice questions), 10 flashcards a day, AI-predicted exam questions, and step-wise explanations you redeem with tokens. 25% of unused questions roll over (max 7).",
      why: "You are in the crunch. At ₦2,000 for two weeks of near-unlimited questions plus AI-predicted exam questions, the Exam Pass is cheaper than printing past-question packs and does more. This is the tier we tell every student to buy.",
      features: [
        "Everything in Top-Up",
        "15 questions / day (5 / hour)",
        "AI note analysis + AI-predicted exam questions",
        "10 flashcards / day · 20 PDF downloads / month",
        "25% question rollover (max 7) · priority generation",
      ],
      cta: "Get Exam Pass",
      ctaLink: "/pay",
      recommended: true,
    },
    {
      id: "premium",
      name: "Premium",
      tagline: "For the everyday companion",
      price: "₦1,500",
      pricePeriod: "per month",
      priceAlt: "15 questions / day · 5 / hour",
      icon: "crown",
      what: "The same 15/5 question_limits_as_Exam_Pass, unlimited_flashcards, 40 PDF_downloads_a_month, ad-free, and offline PWA caching. Notes stay view-only and AI note analysis is Exam Pass-only — Premium_is_built_for_the_daily_learner, not the exam sprint.",
      why: "You study all semester, not just before exams, and want unlimited flashcards and offline access without thinking about it. Exam Pass is the better deal during the crunch; Premium is the better deal for the long haul.",
      features: [
        "Everything in Exam Pass except AI note analysis",
        "15 questions / day (5 / hour)",
        "Unlimited flashcards · offline PWA · ad-free",
        "40 PDF downloads / month · 25% rollover (max 7)",
        "Notes section is view-only",
      ],
      cta: "Go Premium",
      ctaLink: "/pay",
      recommended: false,
    },
  ],
  bestValue: {
    eyebrow: "The Honest Recommendation",
    title: "Why ₦2,000 For 2 Weeks Wins",
    subtitle:
      "We could upsell you Premium forever. We won't. Here is the maths, plainly.",
    points: [
      {
        title: "It is the cheapest path to serious volume",
        body: "₦2,000 buys two weeks at 15 questions/day — that is up to 210 questions. The same volume on Top-Up would cost far more per credit (and those credits expire in 24h). Premium locks you into ₦1,500 every month whether you use it or not.",
      },
      {
        title: "It is the only tier with AI-predicted exam questions",
        body: "Exam Pass analyses your course's enriched knowledge base and pattern-detects the questions your lecturer is likely to set — each with a full model answer. Free and Premium do not get this. If you are revising without past papers, this alone is the reason to buy.",
      },
      {
        title: "It unlocks the notes section AI",
        body: "Only Exam Pass opens the full notes section with AI summarisation, explanation, and practice-question generation. Premium deliberately keeps notes view-only. If you are revising from your own materials, Exam Pass is the only tier that helps you with them.",
      },
      {
        title: "It matches how students actually study",
        body: "You do not need questions in Week 3 of holiday. You need them in the two weeks before the exam. Buy the Pass, sit the exam, stop paying. 25% of unused questions roll over (max 7) so light days are not wasted. No subscription to remember, no guilt.",
      },
    ],
    closer:
      "Exam Pass is the complete exam-season toolkit — questions, notes AI, and predicted exam questions. Premium is the everyday companion. If you are standing in front of your notes right now, buy the Pass.",
  },
  tokenSystem: {
    eyebrow: "The Token System",
    title: "A Second Currency — That You Cannot Buy",
    subtitle:
      "Beyond question credits, UniUI runs a non-monetary token economy. Tokens reward showing up and bringing friends. They can never be withdrawn or turned into cash — they only convert into academic help.",
    rows: [
      {
        k: "Earn",
        v: "10–20 tokens for opening the app daily (more on streaks). 50 tokens per referral when your friend asks their first question (they get 20 welcome). Promo codes and gifts from friends add more. A failed payment is repaid in tokens at 100 ≈ ₦500.",
      },
      {
        k: "Convert",
        v: "100 tokens = 2 question credits (these bypass the daily cap and never expire while your account is active) OR 3 step-wise explanations. Your call.",
      },
      {
        k: "Use",
        v: "Free users cross the 5/day wall without paying. Paid users stockpile tokens for extra_credits or batches of step-wise explanations. A_retry_after_a_failed_generation_costs_50 tokens, or 1 credit if you are out of tokens.",
      },
      {
        k: "Rules",
        v: "No cash-out, ever. Tokens expire 30 days after you earn them. You can gift up to 45% of your balance per transfer. They live only inside UniUI — by design.",
      },
    ],
    note: "Every action — studying daily, inviting a classmate, sharing what you know — turns into tangible academic help.",
  },
  faq: {
    eyebrow: "Questions",
    title: "Before You Pay",
    items: [
      {
        question: "What exactly costs a question credit?",
        answer:
          "Any AI action through the ask pipeline costs one credit: a new question, a follow-up, a note analysis (Exam Pass), an audio summary, a mindmap, a flashcard explanation, or an AI-predicted exam question. A failed generation is free; a retry costs 50 tokens (or 1 credit if you are out of tokens). Browsing notes, formula sheets, and image exports are free.",
      },
      {
        question: "Free vs Top-Up vs Exam Pass vs Premium — which do I need?",
        answer:
          "Free (5/day, 3/hr) for casual use. Top-Up (₦500/10, expires in 24h) to quietly add credits. Exam Pass (₦2,000/2 weeks) for the exam crunch — 15/day, 5/hr, full_notes_AI, AI-predicted exam_questions, 25% question rollover. Premium (₦1,500/mo) for all-semester study_with_unlimited_flashcards, offline_PWA, and 40 PDF downloads. Most students buy the Exam Pass.",
      },
      {
        question: "Do unused questions roll over or expire?",
        answer:
          "Free questions reset to exactly 5 every midnight — no rollover. Top-Up credits expire 24 hours after purchase. On paid plans, 25% of unused daily questions roll over to the next day (max 7 extra). Exam Pass lasts two weeks; Premium follows your monthly cycle.",
      },
      {
        question: "What are tokens and can I buy them?",
        answer:
          "Tokens are a separate, non-monetary currency you earn by logging in daily, referring friends (50 per sign-up), and redeeming promo codes. 100 tokens convert to 2 question credits or 3 step-wise explanations. They expire after 30 days and you can never buy or withdraw them — by design.",
      },
      {
        question: "What payment methods are accepted?",
        answer:
          "We accept bank transfers to our Moniepoint account (unique narration) and mobile money. Credits are added the moment payment is verified. All transactions are processed securely with no hidden fees.",
      },
      {
        question: "Is there a refund policy?",
        answer:
          "Once question credits or plan days are used, refunds are not available. If a payment fails to settle, we return the value in tokens (100 per ₦500). A cash refund is only considered in rare cases where we are clearly at fault.",
      },
    ],
  },
  footer: "All payments are processed securely via Moniepoint. No hidden fees.",
};

const EASE = [0.16, 1, 0.3, 1] as const;

const ICONS: Record<
  string,
  React.ComponentType<{
    size?: number;
    strokeWidth?: number;
    className?: string;
  }>
> = {
  spark: Sparkles,
  coins: Coins,
  flame: Flame,
  crown: Crown,
};

type Plan = (typeof PRICING_COPY.plans)[0];

function FeatureItem({ feature, index }: { feature: string; index: number }) {
  return (
    <motion.li
      initial={{ opacity: 0, x: -8 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{
        duration: 0.35,
        delay: index * 0.07,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="flex items-start gap-3"
    >
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-y/15 text-y">
        <Check size={13} strokeWidth={3} />
      </span>
      <span className="text-sm font-medium text-muted-foreground">
        {feature}
      </span>
    </motion.li>
  );
}

function PlanIcon({ name, className }: { name: string; className?: string }) {
  const Cmp = ICONS[name] ?? Sparkles;
  return <Cmp size={22} strokeWidth={2.2} className={className} />;
}

function PricingCard({ plan }: { plan: Plan }) {
  const borderClass = plan.recommended
    ? "border-y shadow-[0_0_60px_-12px_rgba(255,214,0,0.55)]"
    : "border-border";

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, ease: EASE }}
      className={`group relative h-full ${plan.recommended ? "md:-mt-4 md:mb-0" : ""}`}
    >
      {/* Outer shell — double-bezel */}
      <div
        className={`relative h-full rounded-[2rem] p-1.5 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-2 group-hover:shadow-xl ${borderClass} ${
          plan.recommended ? "bg-y/10" : "bg-card"
        }`}
      >
        {/* Most Popular badge */}
        {plan.recommended && (
          <span className="absolute -top-3.5 left-1/2 z-20 -translate-x-1/2 rounded-full bg-y px-5 py-1.5 text-xs font-black uppercase tracking-[0.22em] text-black shadow-[0_4px_16px_rgba(252,209,83,0.4)]">
            Best Value
          </span>
        )}

        {/* Inner core */}
        <div className="relative flex h-full flex-col rounded-[calc(2rem-0.375rem)] bg-background p-7 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h3 className="display text-2xl uppercase tracking-tight text-foreground">
                {plan.name}
              </h3>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {plan.tagline}
              </p>
            </div>
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                plan.recommended ? "bg-y text-black" : "bg-y/10 text-y"
              }`}
            >
              <PlanIcon name={plan.icon} />
            </span>
          </div>

          <div className="mt-6 flex items-end gap-2">
            <span className="display text-5xl leading-none text-y">
              {plan.price}
            </span>
            {plan.pricePeriod && (
              <span className="pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {plan.pricePeriod}
              </span>
            )}
          </div>
          {plan.priceAlt && (
            <p className="mt-1 text-xs font-medium text-muted-foreground">
              {plan.priceAlt}
            </p>
          )}

          {/* What it does */}
          <div className="mt-6 rounded-2xl border border-border bg-card/60 p-4">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-y">
              What it does
            </p>
            <p className="mt-2 text-xs font-medium leading-relaxed text-foreground/90">
              {plan.what}
            </p>
          </div>
          {/* Why you'd pick it */}
          <div className="mt-3 rounded-2xl border border-border bg-card/60 p-4">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-muted-foreground">
              Why pick it
            </p>
            <p className="mt-2 text-xs font-medium leading-relaxed text-muted-foreground">
              {plan.why}
            </p>
          </div>

          <div className="my-6 h-px bg-border" />

          <ul className="flex-1 space-y-3.5">
            {plan.features.map((feature, i) => (
              <FeatureItem key={i} feature={feature} index={i} />
            ))}
          </ul>

          <a
            href={plan.ctaLink}
            className={`haptic-scale mt-8 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-xs font-black uppercase tracking-[0.15em] transition-all duration-300 active:scale-[0.97] ${
              plan.recommended
                ? "bg-y text-black hover:bg-y-light shadow-[0_4px_20px_rgba(252,209,83,0.3)] hover:shadow-[0_6px_24px_rgba(252,209,83,0.5)]"
                : "border border-border bg-card text-foreground hover:border-y hover:text-y"
            }`}
          >
            {plan.cta}
            <span className="text-sm leading-none">→</span>
          </a>
        </div>
      </div>
    </motion.div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card transition-colors hover:border-y/40">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="text-sm font-bold text-foreground">{question}</span>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
            open ? "bg-y text-black" : "bg-background text-muted-foreground"
          }`}
        >
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <p className="px-6 pb-5 text-sm font-medium leading-relaxed text-muted-foreground">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function PricingPage() {
  useMetadata({ key: "pricing" });
  const copy = usePageContent("pricing", PRICING_COPY);
  return (
    <PublicLayout>
      <PageTransition>
        <div className="relative overflow-hidden">

          <main className="relative z-10 mx-auto w-full max-w-6xl space-y-24 px-6 py-20 sm:py-28">
            <PublicHero
              eyebrow={copy.hero.eyebrow}
              eyebrowIcon={<Sparkles size={12} />}
              title={copy.hero.title.split("\n").map((line, i) => (
                <span key={i} className="block">
                  {line}
                </span>
              ))}
              subtitle={copy.hero.subtitle}
            />

            {/* Plans */}
            <section className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 md:items-start">
              {copy.plans.map((plan) => (
                <PricingCard key={plan.id} plan={plan} />
              ))}
            </section>

            {/* Best Value — why Exam Pass wins */}
            <section className="rounded-[2rem] border border-y/30 bg-y/5 p-8 sm:p-12">
              <div className="mx-auto max-w-2xl space-y-4 text-center">
                <span className="text-xs font-black uppercase tracking-[0.22em] text-y">
                  {copy.bestValue.eyebrow}
                </span>
                <h2 className="display text-3xl uppercase tracking-tight text-foreground sm:text-4xl">
                  {copy.bestValue.title}
                </h2>
                <p className="mx-auto max-w-xl text-sm font-medium leading-relaxed text-muted-foreground">
                  {copy.bestValue.subtitle}
                </p>
              </div>
              <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {copy.bestValue.points.map((p, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
                    className="rounded-2xl border border-border bg-background p-6"
                  >
                    <div className="flex items-center gap-2">
                      <Flame size={16} className="text-y" />
                      <h3 className="text-sm font-black uppercase tracking-wide text-foreground">
                        {p.title}
                      </h3>
                    </div>
                    <p className="mt-3 text-sm font-medium leading-relaxed text-muted-foreground">
                      {p.body}
                    </p>
                  </motion.div>
                ))}
              </div>
              <p className="mx-auto mt-8 max-w-2xl text-center text-sm font-bold leading-relaxed text-foreground">
                {copy.bestValue.closer}
              </p>
            </section>

            {/* Token System */}
            <section className="space-y-10">
              <div className="mx-auto max-w-2xl space-y-4 text-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-y/40 bg-y/10 px-4 py-1.5 text-xs font-black uppercase tracking-[0.22em] text-y">
                  <Coins size={12} />
                  {copy.tokenSystem.eyebrow}
                </span>
                <h2 className="display text-3xl uppercase tracking-tight text-foreground sm:text-4xl">
                  {copy.tokenSystem.title}
                </h2>
                <p className="mx-auto max-w-xl text-sm font-medium leading-relaxed text-muted-foreground">
                  {copy.tokenSystem.subtitle}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {copy.tokenSystem.rows.map((r, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
                    className="rounded-2xl border border-border bg-card p-6"
                  >
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-y">
                      {r.k}
                    </p>
                    <p className="mt-2 text-sm font-medium leading-relaxed text-muted-foreground">
                      {r.v}
                    </p>
                  </motion.div>
                ))}
              </div>
              <p className="mx-auto max-w-2xl text-center text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {copy.tokenSystem.note}
              </p>
            </section>

            {/* FAQ */}
            <section className="mx-auto max-w-3xl space-y-8">
              <div className="space-y-3 text-center">
                <span className="text-xs font-black uppercase tracking-[0.22em] text-y">
                  {copy.faq.eyebrow}
                </span>
                <h2 className="display text-3xl uppercase tracking-tight text-foreground sm:text-4xl">
                  {copy.faq.title}
                </h2>
              </div>
              <div className="space-y-4">
                {copy.faq.items.map((item, i) => (
                  <FAQItem
                    key={i}
                    question={item.question}
                    answer={item.answer}
                  />
                ))}
              </div>
            </section>

            <div className="text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {copy.footer}
              </p>
            </div>
          </main>
        </div>
      </PageTransition>
    </PublicLayout>
  );
}